import { type ApiResponse } from '@types';
import {
  type HubspotDisposition,
  type HubspotMapping
} from '../../../types/integrations/hubspot/hubspotMappingTypes.ts';
import { microServicesRoutes } from '@constants';
import { syncApi } from '@lib/apiClients.ts';
import { handleError } from '@utils/handleAPIError.ts';
import type { IntegrationDisposition, PatchMappingDto } from '../../../types/integrations/genericTypes.ts';

interface UseHubspotMappingsApi {
    getHubspotMappingsByOrgId: (organizationId: string) => Promise<ApiResponse<HubspotMapping>>
    getHubspotCustomDispositions: (
        organizationId: string,
        nonce: string,
    ) => Promise<ApiResponse<IntegrationDisposition[]>>,
    patchHubspotMapping: (
        dispositionId: string,
        patchHubspotMappingDto: PatchMappingDto
    ) => Promise<ApiResponse<HubspotDisposition>>,
    deleteHubspotDisposition: (
        organizationId: string
    ) => Promise<ApiResponse<void>>
}

export function useHubspotMappingsApi (): UseHubspotMappingsApi {

  const rootGetRoute = microServicesRoutes.sync.get.hubspot.mappings;
  const rootPostRoute = microServicesRoutes.sync.post.hubspot.mappings;
  const rootDeleteRoute = microServicesRoutes.sync.delete.hubspot.mappings;

  async function getHubspotMappingsByOrgId (
    organizationId: string
  ): Promise<ApiResponse<HubspotMapping>> {
    try {
      const url = `${rootGetRoute.getMappingsByOrgId}/${organizationId}`;
      const response: ApiResponse<HubspotMapping> = await syncApi.get(url);

      return { data: response.data, success: true };
    } catch (e: unknown) {
      return handleError(
        'Error while getting hubspot mappings.',
        e,
        getHubspotMappingsByOrgId.name
      );
    }
  }

  async function getHubspotCustomDispositions (
    organizationId: string, nonce: string
  ): Promise<ApiResponse<IntegrationDisposition[]>> {
    try {
      const url = `${rootGetRoute.getCustomDispositions}/${organizationId}/${nonce}`;
      const response: ApiResponse<HubspotDisposition[]> = await syncApi.get(url);

      let customCallDisposition = [] as IntegrationDisposition[];

      if (response.data !== undefined) {
        customCallDisposition = response.data.map((disposition) => {
          return {
            id: disposition.id,
            attributes: {
              name: disposition.label,
              outcome: disposition.label,
            },
            type: 'callDisposition',
          };
        });
      }
      return { data: customCallDisposition, success: true };
    } catch (e: unknown) {
      return handleError(
        'Error while getting dispositions.',
        e,
        getHubspotCustomDispositions.name
      );
    }
  }

  async function patchHubspotMapping (
    id: string,
    patchHubspotMappingDto: PatchMappingDto
  ): Promise<ApiResponse<HubspotDisposition>> {
    try {
      const url = `${rootPostRoute.patchMapping}/${id}`;
      const response: ApiResponse<HubspotDisposition> = await syncApi.post(url, patchHubspotMappingDto);

      return { data: response.data, success: true };
    } catch (e: unknown) {
      return handleError(
        'Error while patching hubspot mapping.',
        e,
        patchHubspotMapping.name
      );
    }
  }

  async function deleteHubspotDisposition (organizationId: string): Promise<ApiResponse<void>> {
    try {
      const url = `${rootDeleteRoute.deleteDisposition}/${organizationId}`;

      return await syncApi.delete(url);
    } catch (e: unknown) {
      return handleError(
        'Error while deleting hubspot disposition.',
        e,
        deleteHubspotDisposition.name
      );
    }
  }

  return {
    getHubspotMappingsByOrgId,
    getHubspotCustomDispositions,
    patchHubspotMapping,
    deleteHubspotDisposition
  };
}